import React from 'react'
import { Modal, Spin, Empty, Tag, Button, Image, Tooltip } from 'antd'
import { ShoppingCartOutlined } from '@ant-design/icons'
import { formatPriceByUnit } from '../../../utils/stringUtils'

interface Props {
    open: boolean
    onClose: () => void
    keyword: string
    marketplace: string
    products: any[]
    loading?: boolean
    t: any
    onSelectProduct: (product: any) => void
}

const ModalSearchProductList: React.FC<Props> = ({
    open,
    onClose,
    keyword,
    marketplace,
    products,
    loading = false,
    t,
    onSelectProduct,
}) => {
    const renderTitle = () => (
        <div className="flex flex-wrap items-center justify-between pr-8 gap-2">
            <div className="flex items-center gap-2">
                <span className="text-base font-semibold text-gray-800">
                    {t('cart.search_product_results') || 'Kết quả tìm kiếm sản phẩm'}
                </span>
                {keyword && (
                    <span className="text-sm font-normal text-gray-500">
                        "{keyword}"
                    </span>
                )}
                <Tag color="#ff5000" className="font-semibold uppercase ml-1">
                    {marketplace || 'Taobao'}
                </Tag>
            </div>
            {!loading && products && products.length > 0 && (
                <span className="text-xs text-gray-500 font-normal">
                    Tìm thấy <strong className="text-gray-700">{products.length}</strong> sản phẩm
                </span>
            )}
        </div>
    )

    return (
        <Modal
            centered
            open={open}
            onCancel={onClose}
            title={renderTitle()}
            footer={null}
            width={1050}
            bodyStyle={{ padding: '16px 20px', backgroundColor: '#f9fafb' }}
            destroyOnClose
        >
            {loading ? (
                <div className="py-24 flex flex-col items-center justify-center gap-3">
                    <Spin size="large" />
                    <span className="text-sm text-gray-500">Đang tìm kiếm sản phẩm...</span>
                </div>
            ) : !products || products.length === 0 ? (
                <div className="py-16 text-center bg-white rounded-lg border border-gray-100">
                    <Empty
                        description={
                            <span className="text-gray-500 text-sm">
                                {t('cart.search_product_empty') || 'Không tìm thấy sản phẩm phù hợp. Vui lòng thử từ khóa khác.'}
                            </span>
                        }
                    />
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-h-[68vh] overflow-y-auto pr-1 py-1">
                    {products.map((item: any, index: number) => {
                        const title = item.title || item.subject || item.name || 'Sản phẩm Taobao'
                        const imageUrl =
                            item.mainImageUrl ||
                            item.image ||
                            (item.images && item.images[0]) ||
                            (item.productImages && item.productImages[0]) ||
                            ''
                        const rawPrice =
                            item.salePrice ??
                            item.price ??
                            item.consignPrice ??
                            item.msrp ??
                            (item.priceRangeList && item.priceRangeList[0]?.price) ??
                            0
                        const price = typeof rawPrice === 'string' ? parseFloat(rawPrice) || 0 : rawPrice
                        const miId = item.mi_id || item.miId || item.raw?.mi_id || item.raw?.miId
                        const itemId = item.itemId || item.id || item.oid || item.offerId
                        const originUrl =
                            item.raw?.taoworld_url ||
                            item.itemUrl ||
                            item.url ||
                            item.detailUrl ||
                            (miId
                                ? `https://distributor.taobao.global/apps/source/detail?mi_id=${encodeURIComponent(miId)}`
                                : itemId
                                ? `https://item.taobao.com/item.htm?id=${itemId}`
                                : '')

                        return (
                            <div
                                key={item.id || item.itemId || index}
                                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-200 flex flex-col justify-between group"
                            >
                                <div className="p-3">
                                    <div
                                        className="relative w-full h-48 bg-gray-50 rounded overflow-hidden cursor-pointer flex items-center justify-center"
                                        onClick={() => onSelectProduct(item)}
                                    >
                                        <Image
                                            src={imageUrl}
                                            preview={false}
                                            referrerPolicy="no-referrer"
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                            fallback={require('../../../resources/images/empty-image.png')}
                                        />
                                        <div className="absolute top-2 left-2">
                                            <Tag color="#ff5000" className="m-0 text-[11px] font-medium shadow-sm">
                                                {item.marketplace || marketplace || 'Taobao'}
                                            </Tag>
                                        </div>
                                    </div>

                                    <div className="mt-2.5">
                                        <Tooltip title={title} placement="topLeft">
                                            {originUrl ? (
                                                <a
                                                    href={originUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="block text-gray-800 font-medium hover:text-blue-600 line-clamp-2 text-sm leading-snug min-h-[38px] transition-colors"
                                                >
                                                    {title}
                                                </a>
                                            ) : (
                                                <div className="text-gray-800 font-medium line-clamp-2 text-sm leading-snug min-h-[38px]">
                                                    {title}
                                                </div>
                                            )}
                                        </Tooltip>

                                        <div className="mt-2 flex items-baseline justify-between">
                                            <span className="text-red-600 font-bold text-base">
                                                {formatPriceByUnit(price, 'CNY')}
                                            </span>
                                            {item.shopName && (
                                                <span className="text-[11px] text-gray-400 truncate max-w-[110px]" title={item.shopName}>
                                                    {item.shopName}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="px-3 pb-3 pt-2 border-t border-gray-100 mt-auto">
                                    <Button
                                        size="middle"
                                        type="primary"
                                        icon={<ShoppingCartOutlined />}
                                        className="btn--blue rounded w-full flex items-center justify-center gap-1 font-medium"
                                        onClick={() => onSelectProduct(item)}
                                    >
                                        {t('cart.choose_to_buy') || 'Chọn mua'}
                                    </Button>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
        </Modal>
    )
}

export default ModalSearchProductList
